import { useAdminAccess } from "../hooks/useAdminAccess";
import React, { useEffect, useState } from "react";
import Auth from "../Auth";

const emptyFilters = { type: "actions", actor: "", target: "", action: "", status: "", start: "", end: "" };
const statusLabels = { started: "Started / unresolved", succeeded: "Succeeded", completed: "Completed", denied: "Denied", failed: "Failed", partial: "Partial", unknown: "Unknown" };
const userLink = id => `/admin/user/${encodeURIComponent(id)}`;
const formatTime = value => new Date(value).toISOString().replace("T", " ").replace(/\.\d+Z$/, " UTC");

export default function AdminActionLogs() {
  const { role } = useAdminAccess();
  const view = new URLSearchParams(window.location.search).get("view");
  const initial = { ...emptyFilters, type: ["all", "views"].includes(view) ? view : "actions", target: new URLSearchParams(window.location.search).get("targetId") || "" };
  const [input, setInput] = useState(initial);
  const [filters, setFilters] = useState(initial);
  const [offset, setOffset] = useState(0);
  const [revision, setRevision] = useState(0);
  const [result, setResult] = useState({ logs: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    if (role === "support" && !filters.target.trim()) {
      setResult({ logs: [], total: 0 });
      setError("Enter a customer email or ID to view their activity.");
      setLoading(false);
      return;
    }
    const { start, end, ...query } = filters;
    const endDate = end ? new Date(`${end}T00:00:00Z`) : null;
    if (endDate) endDate.setUTCDate(endDate.getUTCDate() + 1);
    Auth.fetchWithIdToken("/api/admin/activity-logs", {
      ...query, offset, limit: 100,
      ...(start ? { startDate: `${start}T00:00:00.000Z` } : {}),
      ...(endDate ? { endDate: endDate.toISOString() } : {}),
    }).then(data => {
      if (!data.success) throw new Error("Unable to load activity history.");
      if (active) setResult(data);
    }).catch(() => {
      if (active) { setResult({ logs: [], total: 0 }); setError("Unable to load activity history. Please try again."); }
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [filters, offset, revision, role]);
  const field = (name, label, type = "text") => <label>{label}<input type={type} value={input[name]} onChange={e => setInput({ ...input, [name]: e.target.value })} /></label>;
  return <section style={{ margin: 20 }}>
    <h2>Admin Activity</h2>
    <p>Server-recorded actions. Started / unresolved means the action began but its outcome has not been recorded. Failed actions may have external side effects; inspect details before retrying.</p>
    <form onSubmit={e => { e.preventDefault(); setFilters({ ...input }); setOffset(0); setRevision(x => x + 1); }} style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
      <label>Activity<select value={input.type} onChange={e => setInput({ ...input, type: e.target.value, action: "", status: "" })}>
        <option value="actions">Actions</option><option value="all">All activity</option><option value="views">Page views</option>
      </select></label>
      {field("actor", "Admin email or ID")}
      {field("target", "Target email or ID")}
      {input.type !== "views" && <>
      {field("action", "Action contains")}
      <label>Outcome<select value={input.status} onChange={e => setInput({ ...input, status: e.target.value })}>
        <option value="">All outcomes</option>{Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </select></label></>}
      {field("start", "From date (UTC)", "date")}{field("end", "Through date (UTC)", "date")}
      <button type="submit">Filter / Refresh</button>
      <button type="button" onClick={() => { setInput(emptyFilters); setFilters(emptyFilters); setOffset(0); setRevision(x => x + 1); }}>Clear</button>
    </form>
    <p>Action and outcome filters show actions only. Page-view targets are matched from the visited user-page URL.</p>
    {error && <p role="alert">{error}</p>}
    {loading ? <p>Loading activity…</p> : <>
      {!error && !result.logs.length && <p>No activity found.</p>}
      <table style={{ width: "100%", borderCollapse: "collapse" }}><thead><tr>{["Time", "Admin", "Target user", "Activity", "Outcome", "Details"].map(label => <th key={label} style={{ textAlign: "left", padding: 8 }}>{label}</th>)}</tr></thead>
        <tbody>{result.logs.map(log => <tr key={`${log.kind || "action"}:${log.id}`} style={{ borderTop: "1px solid #ddd", verticalAlign: "top" }}>
          <td style={{ padding: 8 }}>{formatTime(log.created_at)}</td>
          <td><a href={userLink(log.actor_id)}>{log.actor_email || log.actor_id}</a></td>
          <td>{log.kind === "view" ? "—" : log.details?.targetKind === "email" ? (log.target_email || "Unresolved email") : <a href={userLink(log.target_id)}>{log.target_email || log.target_id}</a>}</td>
          <td>{log.kind === "view" ? <>Page view<br /><small>{log.path}</small></> : log.action}</td><td>{log.kind === "view" ? "—" : <strong style={{ display: "inline-block", borderRadius: 4, padding: "2px 6px", background: log.status === "succeeded" ? "#dcfce7" : log.status === "failed" ? "#fee2e2" : "#fef3c7", color: "#222" }}>{statusLabels[log.status] || log.status}</strong>}</td>
          <td>{log.kind !== "view" && <details><summary>View details</summary><pre style={{ whiteSpace: "pre-wrap", maxWidth: 600 }}>{JSON.stringify({
            auditId: log.id, requestId: log.request_id, ip: log.ip_address, userAgent: log.user_agent,
            completedAt: log.completed_at, httpStatus: log.http_status,
            details: log.details, before: log.before_state, after: log.after_state,
          }, null, 2)}</pre></details>}</td>
        </tr>)}</tbody>
      </table>
      <div style={{ display: "flex", gap: 16, marginTop: 20 }}>
        <button disabled={!offset} onClick={() => setOffset(offset - 100)}>Previous</button>
        <span>Showing {result.total ? offset + 1 : 0}–{offset + result.logs.length} of {result.total}</span>
        <button disabled={offset + 100 >= result.total} onClick={() => setOffset(offset + 100)}>Next</button>
      </div>
    </>}
  </section>;
}
